import React, { createContext, useContext, useEffect, useState } from 'react'

import mixpanel from 'trackers/mixpanel'

interface Props {
  id: string
  expiresAt?: string | Date
  children: React.ReactNode
}

interface BannerControls {
  dismiss: () => void
  trackCta: (cta: string) => void
}

const AnnouncementBannerContext = createContext<BannerControls | null>(null)

// eslint-disable-next-line react-refresh/only-export-components -- hook is tightly coupled to the AnnouncementBanner component's context
export const useAnnouncementBanner = (): BannerControls => {
  const ctx = useContext(AnnouncementBannerContext)
  if (!ctx) {
    throw new Error(
      'useAnnouncementBanner must be used inside <AnnouncementBanner>',
    )
  }
  return ctx
}

export const AnnouncementBanner: React.FC<Props> = ({
  id,
  expiresAt,
  children,
}) => {
  const storageKey = `announcement-banner-${id}-dismissed`

  const [dismissed, setDismissed] = useState(() => {
    if (expiresAt && new Date() > new Date(expiresAt)) return true
    return !!localStorage.getItem(storageKey)
  })

  useEffect(() => {
    if (dismissed) return
    mixpanel.track('Banner Viewed', { banner_id: id })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- intentionally fire once on mount only

  const controls: BannerControls = {
    dismiss: () => {
      mixpanel.track('Banner Dismissed', { banner_id: id })
      localStorage.setItem(storageKey, 'true')
      setDismissed(true)
    },
    trackCta: (cta: string) => {
      mixpanel.track('Banner CTA Clicked', { banner_id: id, cta })
    },
  }

  if (dismissed) return null

  return (
    <AnnouncementBannerContext.Provider value={controls}>
      {children}
    </AnnouncementBannerContext.Provider>
  )
}
